// BESS history for the УЗЕ tab: SoC (%) + power (kW), chart + table.
// Pulls /forecasting/assets/:id/bess/day?date=YYYY-MM-DD&res=5m (open endpoint).
//
// Доба обирається стрілками або календарем. Ендпоінт віддає АГРЕГОВАНІ дані
// (5-хвилинні відра, сума потужності по всіх PCS, середній SoC): сира доба
// Лучок — це 22,7 тис. точок і 36 МБ, а 16 накладених рядів PCS замість однієї
// кривої установки. Повна деталізація лишилась доступною через `res=raw`.
import i18n from '@/i18n';
import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { API_BASE_URL } from '@/services/api';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';
// BessFleet рендериться ТУТ (а не у station/page.tsx), щоб рефактори сторінки
// не викидали під'єднання УЗЕ-стрічки/панелі. Сам BessFleet повертає null для
// станцій з <2 PCS, тож для одно-PCS станцій (Табаки) нічого не додає.
import BessFleet from '@/components/scada/BessFleet';

interface Pt { time: string; t: number; soc: number | null; power: number | null; mode: string | null; temp: number | null; }
interface DaySummary {
  chargeKwh: number | null; dischargeKwh: number | null;
  minSocPct: number | null; maxSocPct: number | null;
  maxChargePowerKw: number | null; maxDischargePowerKw: number | null;
  unitsSeen: number | null; faultBuckets: number | null;
}

const todayKyiv = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Kyiv' }); // YYYY-MM-DD
// "HH:MM" → десяткова година доби (06:30 → 6.5) для ФІКСОВАНОЇ осі 0–24 год.
const hhmm = (s: string): number => { const [h, m] = String(s).split(':').map(Number); return (h || 0) + (m || 0) / 60; };
const hhLabel = (v: number): string => { const h = Math.floor(v); const m = Math.round((v - h) * 60); return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`; };
const modeLabel = (m: string | null) => m === 'charging' ? i18n.t('ak_scada_charge') : m === 'discharging' ? i18n.t('ak_scada_discharge') : m === 'idle' ? i18n.t('ak_scada_standby_3') : (m ?? '—');

/** Зсув доби на ±1 у часі станції (полудень, щоб не впертись у перехід часу). */
const shiftDay = (iso: string, delta: number): string => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString('en-CA');
};
const dmy = (iso: string) => (iso ? iso.split('-').reverse().join('.') : '');
const n1 = (v: number | null | undefined) => (v == null ? '—' : (v / 1000).toFixed(1));

export default function BessHistory({ assetId }: { assetId?: string }) {
  const ct = useChartTheme();
  const [pts, setPts] = useState<Pt[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [date, setDate] = useState<string>(todayKyiv());
  const [sum, setSum] = useState<DaySummary | null>(null);
  const isToday = date === todayKyiv();

  useEffect(() => {
    if (!assetId) { setErr('no-asset'); return; }
    let cancelled = false;
    setPts(null); setErr(null); setSum(null);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/forecasting/assets/${assetId}/bess/day?date=${date}&res=5m`);
        if (!res.ok) throw new Error(String(res.status));
        const d = await res.json();
        if (!cancelled) setSum(d.summary ?? null);
        const points: Pt[] = (d.points ?? []).map((p: any) => ({
          time: p.time,
          t: hhmm(p.time),
          soc: p.battery?.socPct ?? null,
          power: p.pcs?.outputActivePowerKw ?? null,
          mode: p.status?.derivedPowerStatus ?? p.status?.powerFlow ?? null,
          temp: p.pcs?.innerTempC ?? null,
        }));
        points.sort((a, b) => a.t - b.t);
        if (!cancelled) setPts(points);
      } catch (e: any) {
        if (!cancelled) setErr(e.message || 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [assetId, date]);

  // Навігація добою — у правому слоті картки, щоб не зʼїдати висоту графіка.
  const navBtn = (label: string, onClick: () => void, disabled?: boolean) => (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label}
      style={{
        width: 26, height: 26, borderRadius: 7, lineHeight: 1,
        background: 'transparent', border: `1px solid ${scada.border}`,
        color: disabled ? scada.textDim : scada.text,
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
        fontSize: 13,
      }}>{label}</button>
  );
  const dateNav = (
    <div className="flex items-center" style={{ gap: 6 }}>
      {navBtn('‹', () => setDate(shiftDay(date, -1)))}
      <input type="date" value={date} max={todayKyiv()}
        onChange={(e) => { if (e.target.value) setDate(e.target.value); }}
        style={{
          background: scada.panel2, color: scada.text, border: `1px solid ${scada.border}`,
          borderRadius: 7, fontSize: 12, padding: '3px 6px', colorScheme: 'dark',
        }} />
      {navBtn('›', () => setDate(shiftDay(date, 1)), isToday)}
    </div>
  );

  return (
    <>
      <BessFleet assetId={assetId} />
      <div style={{ marginTop: 16 }}>
      <SectionCard
        title={i18n.t('ak_scada_history_soc_and_power')}
        subtitle={
          sum && (sum.chargeKwh != null || sum.dischargeKwh != null)
            ? i18n.t('ak_scada_charge_discharge_mwh', { d: isToday ? i18n.t('ak_scada_today') : dmy(date), c: n1(sum.chargeKwh), r: n1(sum.dischargeKwh) })
              + (sum.minSocPct != null ? ` · SoC ${Math.round(sum.minSocPct)}–${Math.round(sum.maxSocPct ?? 0)}%` : '')
              + (sum.unitsSeen ? ` · PCS ${sum.unitsSeen}` : '')
            : `${isToday ? i18n.t('ak_scada_for_the_current_day') : dmy(date)} (Europe/Kyiv)`
        }
        right={dateNav}
      >
        {err && <div style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_history_is_not_available_for_this_stat')}</div>}
        {!err && !pts && <div style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_loading')}</div>}
        {pts && pts.length === 0 && (
          <div style={{ color: scada.textMuted, fontSize: 13 }}>
            {i18n.t('ak_scada_no_data_for', { d: isToday ? i18n.t('ak_scada_today_2') : dmy(date) })}
          </div>
        )}

        {pts && pts.length > 0 && (
          <>
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={pts} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
                  <CartesianGrid {...ct.gridProps} />
                  {/* Фіксована шкала 0–24 год (числова вісь): графік завжди займає всю
                      добу незалежно від того, за які години є дані. */}
                  <XAxis {...ct.axis} dataKey="t" type="number" domain={[0, 24]}
                    ticks={[0, 3, 6, 9, 12, 15, 18, 21, 24]} tickFormatter={hhLabel} allowDataOverflow />
                  <YAxis yAxisId="p" {...ct.axis} width={48} label={ct.axisLabel(i18n.t('ak_scada_kw'))} />
                  <YAxis yAxisId="soc" orientation="right" domain={[0, 100]} {...ct.axis} width={48}
                    label={ct.axisLabel('SoC %', 90)} />
                  <Tooltip {...ct.tooltip} cursor={ct.cursorLine} labelFormatter={(v: any) => hhLabel(Number(v))} />
                  {/* SoC — заряд накопичувача: --ok-2, як розряд УЗЕ деінде. */}
                  <Line yAxisId="soc" dataKey="soc" name="SoC, %" stroke={ct.series.ok2} {...ct.line} />
                  <Line yAxisId="p" dataKey="power" name={i18n.t('ak_scada_power_kw')} stroke={ct.series.ok} {...ct.line} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <ChartLegend items={[
              { label: i18n.t('ak_scada_power_kw'), color: ct.series.ok },
              { label: 'SoC, %', color: ct.series.ok2 },
            ]} />

            <div style={{ overflowX: 'auto', maxHeight: 280, marginTop: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
                <thead>
                  <tr style={{ background: scada.panel2, position: 'sticky', top: 0 }}>
                    {[i18n.t('ak_scada_time'), 'SoC, %', i18n.t('ak_scada_power_kw'), i18n.t('ak_scada_mode_2'), i18n.t('ak_scada_temp_c')].map((h) => (
                      <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '7px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...pts].reverse().map((p, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${scada.border}` }}>
                      <td style={{ padding: '6px 12px', color: scada.textDim, fontSize: 12 }}>{p.time}</td>
                      <td style={{ padding: '6px 12px', color: scada.text, fontSize: 12 }}>{p.soc != null ? p.soc.toFixed(1) : '—'}</td>
                      <td style={{ padding: '6px 12px', color: (p.power ?? 0) < 0 ? scada.state.curtailed : scada.state.ok, fontSize: 12 }}>{p.power != null ? p.power.toFixed(1) : '—'}</td>
                      <td style={{ padding: '6px 12px', color: scada.textDim, fontSize: 12 }}>{modeLabel(p.mode)}</td>
                      <td style={{ padding: '6px 12px', color: scada.textMuted, fontSize: 12 }}>{p.temp != null ? p.temp.toFixed(0) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </SectionCard>
      </div>
    </>
  );
}
